// client/src/utils/collapsibleCards.js — pure helpers behind shared/CollapsibleCard:
// remembered open/closed state per card (localStorage, never throws) and the one-line
// summaries the home screens show on phones. No imports, so scripts/test-collapsible-cards.js
// can load it by stripping `export`.

export function cardStorageKey(id) {
    return `brec.card.${id}`;
}

// Saved state for a card: true (open), false (closed) or null (never set / unreadable).
export function readCardOpen(id, storage) {
    try {
        const s = storage === undefined ? window.localStorage : storage;
        if (!s) return null;
        const v = s.getItem(cardStorageKey(id));
        if (v === '1') return true;
        if (v === '0') return false;
        return null;
    } catch {
        return null;
    }
}

export function writeCardOpen(id, open, storage) {
    try {
        const s = storage === undefined ? window.localStorage : storage;
        if (s) s.setItem(cardStorageKey(id), open ? '1' : '0');
    } catch {
        /* private window / blocked storage: state just isn't remembered */
    }
}

// What a card starts as on a phone: what the user last chose, else its default.
export function initialCardOpen(saved, defaultOpen) {
    return saved === null || saved === undefined ? !!defaultOpen : !!saved;
}

// "3 due today", "Nothing due": n with a label, or the empty phrase when n is 0/missing.
export function countSummary(n, label, empty) {
    const k = Number(n) || 0;
    if (!k) return empty || `No ${label}`;
    return `${k} ${label}`;
}

// Join the non-empty parts with " · ".
export function joinSummary(parts) {
    return (parts || []).filter((p) => p !== null && p !== undefined && p !== false && p !== '').join(' · ');
}

// Today panel (GET /api/desk/today) — whole-panel line and urgency.
export function todaySummary(t) {
    if (!t) return '';
    const fu = (t.follow_ups || []).length;
    const leads = (t.new_leads || []).length;
    const dl = (t.deadlines || []).length;
    const parts = [
        fu ? `${fu} follow-up${fu === 1 ? '' : 's'}` : null,
        leads ? `${leads} new lead${leads === 1 ? '' : 's'}` : null,
        dl ? `${dl} deadline${dl === 1 ? '' : 's'}` : null,
        t.deadlines_overdue ? `${t.deadlines_overdue} overdue` : null,
    ];
    return joinSummary(parts) || 'Nothing due';
}

export function todayUrgent(t) {
    if (!t) return false;
    return !!(t.deadlines_overdue || (t.maintenance && t.maintenance.emergencies));
}

// Training card (GET /api/training/summary).
export function trainingSummary(d) {
    if (!d) return '';
    const parts = [
        d.open ? `${d.open} assigned` : 'Nothing assigned',
        d.overdue ? `${d.overdue} overdue` : null,
        d.unread ? `${d.unread} new` : null,
    ];
    return joinSummary(parts);
}

// Client portal: transactions list → "2 transactions".
export function transactionsSummary(list) {
    const n = (list || []).length;
    if (!n) return 'None yet';
    return `${n} transaction${n === 1 ? '' : 's'}`;
}

// Office portal "Waiting for you": the tiles with a count, as "2 open deals · 1 fcra notices due".
export function queueSummary(cards, queue) {
    const q = queue || {};
    return joinSummary((cards || []).filter((c) => q[c.key] > 0).map((c) => `${q[c.key]} ${String(c.title).toLowerCase()}`)) || 'Nothing waiting';
}

// A card's own inner heading is hidden only while it is open as a one-line phone card
// (the row above it already shows the title); on wider screens it always shows.
export function hideInnerHeading(phone, open) {
    return !!(phone && open);
}
